/**
 * "View as" for admins.
 *
 * Lets an admin render the app as a specific staff user or contractor sees it.
 *
 * The stored value is only honoured once the SERVER has confirmed the person is
 * an administrator. Without that check this was a line of localStorage that any
 * signed-in member of staff could write, which is exactly how a colleague
 * reached the contractor portal. The API refuses them the data either way -
 * contractor routes are admin-only - but the app should not offer a door that
 * only fails once you are through it.
 *
 * Kept in one module so every view resolves identity the same way rather than
 * each page reading localStorage with its own conventions.
 */
const KEY = 'jarvis-view-as';

// Set from the server's answer, once, at startup. Defaults to "no" so that a
// page rendering before /auth/me returns never honours an impersonation.
let allowed = false;
export function setViewAsAllowed(isAdmin) { allowed = Boolean(isAdmin); }
export function viewAsAllowed() { return allowed; }

/**
 * The stored value, ungated.
 *
 * Only for the API header. The server verifies the caller is an administrator
 * before honouring it, so sending it is a request, never a grant - and sending
 * it unconditionally removes a race that broke this feature: `allowed` is only
 * set once /auth/me has answered, so the very first call of a page load went
 * without the header, /auth/me came back as the administrator rather than the
 * person being viewed, and the impersonation silently did nothing.
 *
 * Everything that decides what to RENDER uses the gated read below.
 */
export const storedViewAs = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.kind && parsed.id ? parsed : null;
  } catch {
    return null;
  }
};

export const storedViewAsUserId = () => {
  const v = storedViewAs();
  return v && v.kind === 'user' ? v.id : null;
};

export const storedViewAsContractorId = () => {
  const v = storedViewAs();
  return v && v.kind === 'contractor' ? v.id : null;
};

/**
 * THE ONE QUESTION THE ROUTER ASKS: is an administrator looking through a
 * contractor's portal, and at whom?
 *
 * App.js had four spellings of it - storedViewAsContractorId(),
 * isViewingAs('contractor'), storedViewAs()?.kind === 'contractor' and
 * portalContractor().viewing - and they did not all agree on the first
 * render: the gated reads answer null until /auth/me has confirmed the
 * administrator, so /contractor-dashboard was decided differently from the
 * routes beside it for a moment after every reload. One name, ungated, for
 * the reason the ungated read above exists: it decides what to DRAW, and the
 * server still refuses the data to anybody who is not an administrator.
 */
export const viewingContractorId = storedViewAsContractorId;

const read = () => {
  if (!allowed) return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.kind || !parsed.id) return null;
    return parsed;
  } catch {
    return null;   // unreadable or blocked storage behaves as "not impersonating"
  }
};

export const getViewAs = read;

export const isViewingAs = (kind) => {
  const v = read();
  return !!v && (!kind || v.kind === kind);
};

/** Drop the impersonation without navigating. Used to recover from a bad one. */
export function clearViewAs() {
  try { localStorage.removeItem(KEY); } catch { /* storage unavailable */ }
}

export function setViewAs(next) {
  try {
    if (!next) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable: view-as simply does not persist */
  }
  // A full reload is deliberate. Every page resolves identity on mount, and a
  // soft state change would leave half the tree showing the previous person.
  window.location.assign(next && next.kind === 'contractor'
    ? '/contractor-dashboard'
    : next ? '/my-tickets' : '/');
}

/** The staff user id the app should act as, or null for the real session. */
export const viewAsUserId = () => {
  const v = read();
  return v && v.kind === 'user' ? v.id : null;
};

/** The contractor id the app should act as, or null. */
export const viewAsContractorId = () => {
  const v = read();
  return v && v.kind === 'contractor' ? v.id : null;
};

export const viewAsLabel = () => {
  const v = read();
  return v ? v.name || v.id : null;
};
